/**
 * Column sort cycle logic, kept separate from the component so it can be tested
 * without a DOM.
 */
export const DEFAULT_SORT_STATE = { sortBy: null, sortOrder: null };
/**
 * Three-state cycle when the header for column `key` is activated.
 *
 * The third state returns to the default order rather than getting stuck
 * between ASC and DESC, so users have a way back without reloading the page.
 *
 * | Current | Activate `key` | Next |
 * |---|---|---|
 * | default | `key` | `{ key, defaultDirection }` |
 * | `{ key, DESC }` | `key` | `{ key, ASC }` |
 * | `{ key, ASC }` | `key` | default |
 * | `{ other, * }` | `key` | `{ key, defaultDirection }` |
 */
export function cycleSortState(current, key, defaultDirection = 'DESC') {
    if (current.sortBy !== key) {
        return { sortBy: key, sortOrder: defaultDirection };
    }
    const opposite = defaultDirection === 'DESC' ? 'ASC' : 'DESC';
    if (current.sortOrder === defaultDirection) {
        return { sortBy: key, sortOrder: opposite };
    }
    return { ...DEFAULT_SORT_STATE };
}
/**
 * `aria-sort` value for a header cell, per the WAI-ARIA specification.
 *
 * A sortable but inactive column must report `"none"` rather than omitting the
 * attribute, so screen readers announce that the column can be sorted.
 */
export function ariaSortValue(columnKey, activeKey, direction) {
    if (columnKey !== activeKey || !direction)
        return 'none';
    return direction === 'ASC' ? 'ascending' : 'descending';
}
/**
 * Assistive-technology label describing both the current state and the outcome
 * of the next activation. The column name alone does not tell a screen reader
 * user what pressing the header will do.
 */
export function sortButtonAriaLabel(options) {
    const { label, columnKey, activeKey, direction, defaultDirection = 'DESC', } = options;
    const isActive = columnKey === activeKey && Boolean(direction);
    if (!isActive) {
        const next = defaultDirection === 'ASC' ? 'ascending' : 'descending';
        return `${label}, not sorted, activate to sort ${next}`;
    }
    if (direction === 'ASC') {
        return `${label}, sorted ascending, activate to remove sorting`;
    }
    return `${label}, sorted descending, activate to sort ascending`;
}
